import { ArrowDownToLine, Minus, MoreHorizontalIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import Spinner from "@/components/Spinner";
import { UseInfiniteQueryResult } from "@tanstack/react-query";
import { memo, useCallback, useState } from "react";
import { ButtonGroup } from "@/components/ui/button-group";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import appConfiguration from "@/components/cfg/AppCfg";

interface ListLoadMoreButtonProps {
  isFetching: UseInfiniteQueryResult["isFetching"];
  hasNextPage: UseInfiniteQueryResult["hasNextPage"];
  fetchNextPage: UseInfiniteQueryResult["fetchNextPage"];
}

export const ListLoadMoreButton = memo(
  ({ isFetching, hasNextPage, fetchNextPage }: ListLoadMoreButtonProps) => {
    const { t } = useTranslation();
    const [paginationLimit, setPaginationLimit] = useState(
      appConfiguration.paginationLimit,
    );

    const onLoadMore = useCallback(() => {
      hasNextPage && fetchNextPage();
    }, [fetchNextPage, hasNextPage]);

    const onChange = useCallback((paginationLimit: number) => {
      appConfiguration.paginationLimit = paginationLimit;
      setPaginationLimit(paginationLimit);
    }, []);

    const isSelected = useCallback(
      (option: number) => paginationLimit === option,
      [paginationLimit],
    );

    return (
      <ButtonGroup>
        <Button
          variant="secondary"
          onClick={onLoadMore}
          disabled={!hasNextPage || isFetching}
          size="sm"
        >
          {isFetching ? (
            <>
              <Spinner className="w-4 h-4" />
              <span>{t("table.loading")}</span>
            </>
          ) : (
            <>
              {hasNextPage ? (
                <>
                  <ArrowDownToLine className="w-4 h-4" />
                  {t("table.loadMore")}
                </>
              ) : (
                <>
                  <Minus className="w-4 h-4" />
                  {t("table.noMoreData")}
                </>
              )}
            </>
          )}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="secondary" size="sm" aria-label="More Options">
              <MoreHorizontalIcon />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            {appConfiguration.paginationLimitOptions.map((option) => (
              <DropdownMenuCheckboxItem
                checked={isSelected(option)}
                onCheckedChange={() => onChange(option)}
                key={option}
              >
                {option}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </ButtonGroup>
    );
  },
);
